import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedDocumentUploadURLForRespondentSpocMappingRPC implements
  LeoRPC<GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Request, GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Response, GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Response, GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.Errors.Errors>>;
}

export namespace GetPresignedDocumentUploadURLForRespondentSpocMappingRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileAttributes: FileAttributesDTO;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDocumentUploadURLForRespondentSpocMappingRPCRequest: boolean = true;
  
    readonly fileAttributes: FileAttributes;
    readonly projectId: LeoUUID;
  
  
    constructor(
    fileAttributes: FileAttributes, 
    projectId: LeoUUID
    ) {
      this.fileAttributes = fileAttributes;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        fileAttributes,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileAttributes: this.fileAttributes.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForRespondentSpocMappingRPC(fileAttributes='${this.fileAttributes}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDocumentUploadURLForRespondentSpocMappingResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForRespondentSpocMapping(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SpocAssignmentInProgressDTO {
      code: string;
    }
    
    export class SpocAssignmentInProgress extends LeoRPCError<RPCError> {
      readonly isSpocAssignmentInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SpocAssignmentInProgress;
        super(code);
      }
    
    
      toDTO(): SpocAssignmentInProgressDTO {
        const dto: SpocAssignmentInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SpocAssignmentInProgress.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.SPOC_ASSIGNMENT_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | SpocAssignmentInProgress;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | SpocAssignmentInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SpocAssignmentInProgress:
          return new SpocAssignmentInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSpocAssignmentInProgressError extends GetPresignedDocumentUploadURLForRespondentSpocMappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocAssignmentInProgressError";
  }
}

